import { useId, type ReactElement, type ReactNode, cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './utils';

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  /** i18n key or message */
  error?: string | undefined;
  className?: string;
  children: ReactElement<{ id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }>;
}

/** Label + control + error, wired for screen readers. */
export function Field({ label, hint, error, className, children }: FieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const errId = `${id}-err`;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-[13px] font-medium text-muted">
        {label}
      </label>
      {cloneElement(children, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errId : undefined,
      })}
      {hint && !error && <p className="text-xs text-muted">{hint}</p>}
      {error && (
        <p id={errId} role="alert" className="text-xs font-medium text-gap">
          {t(error, { defaultValue: error })}
        </p>
      )}
    </div>
  );
}
